import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Grid,
  DialogActions,
  Button,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import { useForm, FormProvider } from "react-hook-form";
import ReplayIcon from "@mui/icons-material/Replay";
import { useDispatch } from "react-redux";

import CustomCheckBox from "../../../../components/CustomCheckBox";
import useUser from "../../../../hooks/useUser";
import useRadUserPlan from "../../../../hooks/useRadUserPlan";
import { useDialog } from "../../../../contexts/DialogContext";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../../redux/slices/snackbar";

function AssignPlan(props) {
  const [ratesMap, setRatesMap] = useState({});
  const dispatch = useDispatch();
  const { getAllRadUserAssignedPlans, addRadUserPlan } = useRadUserPlan();
  const [selectedPlan, setSelectedPlan] = useState(0);
  const [selectedRate, setSelectedRate] = useState(0);
  const { user } = useUser();
  const [planGroupPlanOptions, setPlanGroupPlanOptions] = useState([]);

  const { validationFields, userId } = props;
  const { t } = useTranslation();

  const methods = useForm({});

  const {
    reset,
    formState: { errors },
    control,
    watch,
    handleSubmit,
    setValue,
    resetField,
  } = methods;

  const { unSetDialog, openDialog } = useDialog();

  useEffect(() => {
    if (user && Object.keys(user).length != 0) {
      if (user?.tenantId != undefined) {
        setPlanGroupPlanOptions([]);
        setSelectedPlan(user.planId);
        setSelectedRate(user.rateId);
      }
    } else {
      setPlanGroupPlanOptions([]);
    }
  }, [user]);

  const fetchAssignedPlans = async () => {
    if (user?.tenantId) {
      const payload = {
        filters: [
          {
            fieldName: "radUserId",
            fieldValue: userId,
            op: "eq",
            dataType: "int",
            condition: "and",
          },
          {
            fieldName: "tenantId",
            fieldValue: user.tenantId,
            op: "eq",
            dataType: "int",
            condition: "and",
          },
        ],
        pagination: { pageNumber: 1, pageSize: 1000 },
        sortFields: [],
      };

      const data = await getAllRadUserAssignedPlans(payload);

      if (data) {
        const planGroups = {};
        const newPlanGroupPlanOptions = [];
        const seenPlans = new Set();

        data.forEach((item) => {
          if (!seenPlans.has(item.planId)) {
            newPlanGroupPlanOptions.push({
              label: item.planName,
              value: item.planId,
            });
            seenPlans.add(item.planId);
          }

          if (!planGroups[item.planId]) planGroups[item.planId] = [];
          planGroups[item.planId].push({
            label: item.rateName,
            value: item.rateId,
            assigned: item.assigned,
          });
        });

        // Update State
        setPlanGroupPlanOptions(
          newPlanGroupPlanOptions.sort((a, b) => a.label.localeCompare(b.label)),
        );
        setRatesMap(planGroups);

        // Set Form Values
        Object.keys(planGroups).forEach((planId) => {
          const rates = planGroups[planId];
          const allAssigned = rates.every((r) => r.assigned);

          if (allAssigned) {
            setValue(`assigned-plan-${planId}`, true);
          }

          rates.forEach((r) => {
            if (r.assigned) {
              setValue(`assigned-rate-${r.value}`, true);
            }
          });
        });
      }
    }
  };

  const fetchUser = async () => {
    await getUser(userId);
  };

  useEffect(() => {
    fetchAssignedPlans();
  }, [userId, user]);

  const onSubmit = async (data) => {
    try {
      let radUserPlans = [];

      planGroupPlanOptions.forEach((plan) => {
        const rates = ratesMap[plan.value];
        if (rates) {
          rates.forEach((rate) => {
            if (watch(`assigned-rate-${rate.value}`)) {
              radUserPlans.push({
                plan: {
                  planId: plan.value,
                  planName: "", // Optional: Include plan name if needed, but backend seems to rely on ID
                },
                rate: {
                  rateId: rate.value,
                  rateName: rate.label,
                },
              });
            }
          });
        }
      });

      if (radUserPlans.length == 0) {
        dispatch(showErrorSnackbar("Please select atleast one plan"));
      } else {
        let payload = {
          radUser: {
            radUserId: userId,
          },
          radUserPlans: radUserPlans,
        };
        await addRadUserPlan(payload);
        dispatch(showSuccessSnackbar("Plan Assigned successfully!"));
        unSetDialog("assign_plan");
      }
    } catch (ex) {
      dispatch(showErrorSnackbar(ex.message));
    }
  };

  return (
    <React.Fragment>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3} className="row" pt={8} sx={{ paddingLeft: "16px" }}>
            <Grid item md={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 2,
                  bgcolor: "background.paper",
                  borderRadius: 1,
                  boxShadow: 1,
                  mb: 2,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        planGroupPlanOptions.length > 0 &&
                        planGroupPlanOptions.every((plan) => watch(`assigned-plan-${plan.value}`))
                      }
                      indeterminate={
                        planGroupPlanOptions.some((plan) => watch(`assigned-plan-${plan.value}`)) &&
                        !planGroupPlanOptions.every((plan) => watch(`assigned-plan-${plan.value}`))
                      }
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        planGroupPlanOptions.forEach((plan) => {
                          setValue(`assigned-plan-${plan.value}`, isChecked);
                          ratesMap[plan.value]?.forEach((rate) => {
                            setValue(`assigned-rate-${rate.value}`, isChecked);
                          });
                        });
                      }}
                    />
                  }
                  label={
                    <Typography variant="subtitle1" fontWeight="bold">
                      Select All Plans & Rates
                    </Typography>
                  }
                />
              </Box>
            </Grid>

            {planGroupPlanOptions.map((planGroupPlanOption) => {
              const planRates = ratesMap[planGroupPlanOption.value] || [];
              const allRatesChecked =
                planRates.length > 0 &&
                planRates.every((rate) => watch(`assigned-rate-${rate.value}`));
              const someRatesChecked =
                planRates.some((rate) => watch(`assigned-rate-${rate.value}`)) && !allRatesChecked;

              return (
                <Grid item md={12} key={planGroupPlanOption.value}>
                  <Card variant="outlined" sx={{ overflow: "visible" }}>
                    <Box
                      sx={{
                        bgcolor: "action.hover",
                        px: 2,
                        py: 1,
                        display: "flex",
                        alignItems: "center",
                        borderBottom: 1,
                        borderColor: "divider",
                      }}
                    >
                      <CustomCheckBox
                        defaultValue={true}
                        label={planGroupPlanOption.label}
                        name={`assigned-plan-${planGroupPlanOption.value}`}
                        type="checkbox"
                        fields={validationFields}
                        my={0} // Minimize vertical margin in header
                        fullWidth
                        errors={errors}
                        indeterminate={someRatesChecked}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          planRates.forEach((rate) => {
                            setValue(`assigned-rate-${rate.value}`, isChecked);
                          });
                        }}
                      />
                    </Box>
                    <CardContent sx={{ pt: 2, pb: "16px !important" }}>
                      <Grid container spacing={2}>
                        {planRates.map((rate) => (
                          <Grid item md={4} sm={6} xs={12} key={rate.value}>
                            <Box
                              sx={{
                                border: 1,
                                borderColor: "divider",
                                borderRadius: 1,
                                p: 1,
                                px: 2,
                                display: "flex",
                                alignItems: "center",
                                height: "100%",
                                "&:hover": {
                                  bgcolor: "action.hover",
                                },
                              }}
                            >
                              <CustomCheckBox
                                defaultValue={true}
                                label={rate.label}
                                name={`assigned-rate-${rate.value}`}
                                type="checkbox"
                                fields={validationFields}
                                my={0}
                                fullWidth
                                errors={errors}
                                onChange={(e) => {
                                  const isChecked = e.target.checked;
                                  const otherRates = planRates.filter(
                                    (r) => r.value !== rate.value,
                                  );
                                  const allOthersChecked = otherRates.every((r) =>
                                    watch(`assigned-rate-${r.value}`),
                                  );

                                  if (isChecked && allOthersChecked) {
                                    setValue(`assigned-plan-${planGroupPlanOption.value}`, true);
                                  } else if (!isChecked) {
                                    setValue(`assigned-plan-${planGroupPlanOption.value}`, false);
                                  }
                                }}
                              />
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          <DialogActions>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<ReplayIcon />}
              endIcon={false ? <CircularProgress size={24} /> : null}
              disabled={false}
            >
              Assign
            </Button>

            <Button variant="text" onClick={() => unSetDialog("assign_plan")} color="primary">
              Close
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </React.Fragment>
  );
}

export default AssignPlan;
